import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';

import type { ApiError } from '@/core/api';
import * as api from './api';
import type { CompanyInput } from './types';

export const companyKeys = {
  all: ['companies'] as const,
  lists: () => [...companyKeys.all, 'list'] as const,
  list: (params: api.CompanyListParams) => [...companyKeys.lists(), params] as const,
  detail: (id: string | number) => [...companyKeys.all, 'detail', id] as const,
  technologies: () => [...companyKeys.all, 'technologies'] as const,
};

export function useCompanyTechnologies() {
  return useQuery({
    queryKey: companyKeys.technologies(),
    queryFn: api.listCompanyTechnologies,
    staleTime: 60_000,
  });
}

export function useCompanies(params: api.CompanyListParams) {
  return useQuery({
    queryKey: companyKeys.list(params),
    queryFn: () => api.listCompanies(params),
    placeholderData: keepPreviousData, // keep prior page visible while fetching next
  });
}

export function useCompany(id: string | number) {
  return useQuery({
    queryKey: companyKeys.detail(id),
    queryFn: () => api.getCompany(id),
    enabled: Boolean(id),
  });
}

export function useCreateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CompanyInput) => api.createCompany(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: companyKeys.lists() });
      qc.invalidateQueries({ queryKey: companyKeys.technologies() });
      toast.success('Company created');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message); // field errors go to the form
    },
  });
}

export function useUpdateCompany(id: string | number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<CompanyInput>) => api.updateCompany(id, input),
    onSuccess: (company) => {
      qc.invalidateQueries({ queryKey: companyKeys.lists() });
      qc.invalidateQueries({ queryKey: companyKeys.technologies() });
      qc.setQueryData(companyKeys.detail(id), company);
      toast.success('Company updated');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useArchiveCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => api.archiveCompany(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: companyKeys.lists() });
      toast.success('Company archived');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useRestoreCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => api.restoreCompany(id),
    onSuccess: (company) => {
      qc.invalidateQueries({ queryKey: companyKeys.lists() });
      qc.setQueryData(companyKeys.detail(company.public_id), company);
      toast.success('Company restored');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function usePurgeCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => api.purgeCompany(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: companyKeys.lists() });
      toast.success('Company permanently deleted');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}
